import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';

function formatMoney(v) {
  const n = Number(String(v || '0').replace(',', '.'));
  if (!Number.isFinite(n)) return v || '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function RateioConsolidadoDialog({
  open,
  onClose,
  prestador,
  unidadeSelecionada,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!prestador?.id) return;
    setLoading(true);
    setError('');
    try {
      const url = withUnidade(
        `/api/prestadores/${prestador.id}/rateio-consolidado`,
        unidadeSelecionada
      );
      const res = await apiFetch(url);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar consolidado');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [prestador?.id, unidadeSelecionada]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Consolidado do rateio — {prestador?.numeroContrato}
        {data?.resumo && (
          <Typography variant="body2" color="text.secondary">
            Total {formatMoney(data.resumo.valorTotal)} · Pago {formatMoney(data.resumo.valorPago)} ·
            Pendente {formatMoney(data.resumo.valorPendente)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Unidade</TableCell>
                <TableCell>Fatia</TableCell>
                <TableCell>Pagamentos</TableCell>
                <TableCell>Documentos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.unidades || []).map((u) => (
                <TableRow key={u.rateioId || u.unidade}>
                  <TableCell>
                    <Typography fontWeight={600}>{u.unidade}</Typography>
                    {u.percentual != null && (
                      <Typography variant="caption" color="text.secondary">
                        {Number(u.percentual).toFixed(1)}%
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatMoney(u.valorMesUnidade)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={`${u.parcelas?.pagas || 0}/${u.parcelas?.total || 0} pagas`}
                      color={
                        u.parcelas?.total && u.parcelas.pagas === u.parcelas.total
                          ? 'success'
                          : 'default'
                      }
                    />
                    <Typography variant="caption" display="block">
                      {formatMoney(u.parcelas?.valorPago)} pago
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(u.documentos || []).length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                    {(u.documentos || []).map((d, i) => (
                      <Box key={i} sx={{ mb: 0.5 }}>
                        <Typography variant="caption">Parc. {d.parcela}</Typography>
                        {d.notaFiscalUrl && (
                          <ButtonDoc
                            label="NF"
                            onClick={() => openAuthenticatedUpload(d.notaFiscalUrl)}
                          />
                        )}
                        {d.comprovanteUrl && (
                          <ButtonDoc
                            label="Comp."
                            onClick={() => openAuthenticatedUpload(d.comprovanteUrl)}
                          />
                        )}
                      </Box>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

function ButtonDoc({ label, onClick }) {
  return (
    <Button size="small" onClick={onClick} sx={{ minWidth: 0, px: 0.5 }}>
      {label}
    </Button>
  );
}
